import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ACCESSORIES,
  ALL_CABINET_TYPES,
  CABINET_TYPE_DEFAULTS,
  SUGGESTED_CONFLICT_GROUPS,
  type AccessoryDefinition,
  type CabinetType,
} from "@/data/cabinetDefaults";
import {
  upsertAccessoryOverride,
  deleteAccessoryOverride,
  loadAccessoryOverrides,
  isBuiltinAccessoryId,
  fetchAccessoryOverrides,
} from "@/lib/accessoriesCatalog";

export type EditorTarget =
  | { mode: "create"; presetCabinetTypes?: CabinetType[] }
  | { mode: "edit"; id: string };

interface Props {
  open: boolean;
  target: EditorTarget | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

type FormState = {
  id: string;
  label: string;
  cabinet_types: CabinetType[];
  min_width: string;
  max_width: string;
  conflict_group: string;
  description: string;
  price: string;
  requires_standard_depth: boolean;
  requires_door_drawer: boolean;
  shallow_drawer_insert: boolean;
  deep_drawer_insert: boolean;
  requires_side_selection: boolean;
  is_custom_request: boolean;
  allow_multiple: boolean;
  max_quantity: string;
};

const blank = (presetTypes?: CabinetType[]): FormState => ({
  id: "",
  label: "",
  cabinet_types: presetTypes ?? [],
  min_width: "",
  max_width: "",
  conflict_group: "",
  description: "",
  price: "0",
  requires_standard_depth: false,
  requires_door_drawer: false,
  shallow_drawer_insert: false,
  deep_drawer_insert: false,
  requires_side_selection: false,
  is_custom_request: false,
  allow_multiple: false,
  max_quantity: "",
});

const fromAccessory = (a: AccessoryDefinition): FormState => ({
  id: a.id,
  label: a.label,
  cabinet_types: a.cabinetTypes,
  min_width: a.minWidth != null ? String(a.minWidth) : "",
  max_width: a.maxWidth != null ? String(a.maxWidth) : "",
  conflict_group: a.conflictGroup ?? "",
  description: a.description ?? "",
  price: String(a.price ?? 0),
  requires_standard_depth: !!a.requiresStandardDepth,
  requires_door_drawer: !!a.requiresDoorDrawer,
  shallow_drawer_insert: !!a.shallowDrawerInsert,
  deep_drawer_insert: !!a.deepDrawerInsert,
  requires_side_selection: !!a.requiresSideSelection,
  is_custom_request: !!a.isCustomRequest,
  allow_multiple: !!a.allowMultiple,
  max_quantity: a.maxQuantity != null ? String(a.maxQuantity) : "",
});

const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);

const AccessoryEditorDialog = ({ open, target, onOpenChange, onSaved }: Props) => {
  const [form, setForm] = useState<FormState>(blank());
  const [saving, setSaving] = useState(false);
  const isEdit = target?.mode === "edit";
  const isBuiltin = isEdit && isBuiltinAccessoryId(target!.id);

  useEffect(() => {
    if (!open || !target) return;
    if (target.mode === "edit") {
      const acc = ACCESSORIES.find((a) => a.id === target.id);
      setForm(acc ? fromAccessory(acc) : blank());
    } else {
      setForm(blank(target.presetCabinetTypes));
    }
  }, [open, target]);

  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const toggleCabinetType = (t: CabinetType) =>
    setForm((p) => ({
      ...p,
      cabinet_types: p.cabinet_types.includes(t)
        ? p.cabinet_types.filter((x) => x !== t)
        : [...p.cabinet_types, t],
    }));

  const handleSave = async () => {
    const label = form.label.trim();
    if (!label) return toast.error("Label is required");
    if (form.cabinet_types.length === 0) return toast.error("Pick at least one cabinet type");

    const priceNum = Number(form.price);
    if (!Number.isFinite(priceNum) || priceNum < 0) return toast.error("Invalid price");

    let id = form.id;
    if (!isEdit) {
      const base = slugify(label) || `acc_${Date.now()}`;
      id = base;
      // Avoid clobbering an existing id
      const existing = await fetchAccessoryOverrides();
      const taken = new Set([...ACCESSORIES.map((a) => a.id), ...existing.map((r) => r.id)]);
      if (taken.has(id)) {
        let n = 2;
        while (taken.has(`${base}_${n}`)) n++;
        id = `${base}_${n}`;
      }
    }

    setSaving(true);
    try {
      await upsertAccessoryOverride({
        id,
        label,
        cabinet_types: form.cabinet_types,
        min_width: form.min_width ? Number(form.min_width) : null,
        max_width: form.max_width ? Number(form.max_width) : null,
        requires_standard_depth: form.requires_standard_depth,
        requires_door_drawer: form.requires_door_drawer,
        shallow_drawer_insert: form.shallow_drawer_insert,
        deep_drawer_insert: form.deep_drawer_insert,
        requires_side_selection: form.requires_side_selection,
        is_custom_request: form.is_custom_request,
        allow_multiple: form.allow_multiple,
        max_quantity: form.allow_multiple && form.max_quantity ? Number(form.max_quantity) : null,
        conflict_group: form.conflict_group.trim() || null,
        description: form.description.trim() || null,
        price: priceNum,
        sort_order: 0,
        hidden: false,
        is_custom: !isBuiltinAccessoryId(id),
      });
      await loadAccessoryOverrides(true);
      toast.success(isEdit ? "Accessory updated" : "Accessory added");
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleHideOrDelete = async () => {
    if (!isEdit) return;
    const id = target!.id;
    if (isBuiltin) {
      if (!confirm(`Hide built-in accessory "${form.label}" from the catalog?`)) return;
    } else {
      if (!confirm(`Delete custom accessory "${form.label}"? This cannot be undone.`)) return;
    }
    setSaving(true);
    try {
      if (isBuiltin) {
        // Hide via an override row.
        await upsertAccessoryOverride({
          id,
          label: null,
          cabinet_types: null,
          min_width: null,
          max_width: null,
          requires_standard_depth: null,
          requires_door_drawer: null,
          shallow_drawer_insert: null,
          deep_drawer_insert: null,
          requires_side_selection: null,
          is_custom_request: null,
          allow_multiple: null,
          max_quantity: null,
          conflict_group: null,
          description: null,
          price: null,
          sort_order: 0,
          hidden: true,
          is_custom: false,
        });
      } else {
        await deleteAccessoryOverride(id);
      }
      await loadAccessoryOverrides(true);
      toast.success(isBuiltin ? "Accessory hidden" : "Accessory deleted");
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreBuiltin = async () => {
    if (!isEdit || !isBuiltin) return;
    if (!confirm("Restore this accessory to its built-in defaults?")) return;
    setSaving(true);
    try {
      await deleteAccessoryOverride(target!.id);
      await loadAccessoryOverrides(true);
      toast.success("Restored to built-in defaults");
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit "${form.label || target?.id}"` : "Add accessory"}</DialogTitle>
          <DialogDescription>
            Configure the rules that determine when this accessory is offered and how it's priced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Label</Label>
              <Input value={form.label} onChange={(e) => setField("label", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Price ($)</Label>
              <Input
                type="number"
                step="1"
                value={form.price}
                onChange={(e) => setField("price", e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cabinet types</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 p-3 border border-border">
              {ALL_CABINET_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.cabinet_types.includes(t)}
                    onCheckedChange={() => toggleCabinetType(t)}
                  />
                  <span>{CABINET_TYPE_DEFAULTS[t].label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Min width (inches)
              </Label>
              <Input
                type="number"
                value={form.min_width}
                onChange={(e) => setField("min_width", e.target.value)}
                placeholder="No minimum"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Max width (inches)
              </Label>
              <Input
                type="number"
                value={form.max_width}
                onChange={(e) => setField("max_width", e.target.value)}
                placeholder="No maximum"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Conflict group
            </Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              Only one accessory per group can be selected on a cabinet. Pick an existing group or create a new one.
            </p>
            {(() => {
              const inUse = ACCESSORIES.map((a) => a.conflictGroup).filter(
                (g): g is string => !!g,
              );
              const suggestedKeys = SUGGESTED_CONFLICT_GROUPS.map((s) => s.key);
              const allGroups = Array.from(new Set([...inUse, ...suggestedKeys])).sort();
              const descriptionFor = (key: string) =>
                SUGGESTED_CONFLICT_GROUPS.find((s) => s.key === key)?.description;
              const isExisting =
                !form.conflict_group || allGroups.includes(form.conflict_group);
              const selectValue = !form.conflict_group
                ? "__none__"
                : allGroups.includes(form.conflict_group)
                ? form.conflict_group
                : "__custom__";
              return (
                <div className="space-y-2">
                  <Select
                    value={selectValue}
                    onValueChange={(v) => {
                      if (v === "__none__") setField("conflict_group", "");
                      else if (v === "__custom__") setField("conflict_group", " ");
                      else setField("conflict_group", v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a conflict group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (no conflicts)</SelectItem>
                      {allGroups.map((g) => {
                        const desc = descriptionFor(g);
                        return (
                          <SelectItem key={g} value={g}>
                            <span className="font-mono text-xs">{g}</span>
                            {desc && (
                              <span className="text-muted-foreground"> — {desc}</span>
                            )}
                          </SelectItem>
                        );
                      })}
                      <SelectItem value="__custom__">Custom (new group)...</SelectItem>
                    </SelectContent>
                  </Select>

                  {!isExisting && (
                    <Input
                      autoFocus
                      value={form.conflict_group.trim()}
                      onChange={(e) => setField("conflict_group", e.target.value)}
                      placeholder="e.g. corner_base_organizer"
                    />
                  )}
                </div>
              );
            })()}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2 border border-border p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Rules</p>
            {([
              ["requires_door_drawer", "Requires door + drawer front config"],
              ["requires_standard_depth", "Requires standard cabinet depth"],
              ["shallow_drawer_insert", "Lives in shallow (top) drawer"],
              ["deep_drawer_insert", "Lives in deep (bottom) drawer"],
              ["requires_side_selection", "Requires left / right side selection"],
              ["is_custom_request", "Custom request — contractor adds a description"],
              ["allow_multiple", "Allow adding more than one per cabinet"],
            ] as const).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form[key] as boolean}
                  onCheckedChange={(v) => setField(key, !!v)}
                />
                <span>{label}</span>
              </label>
            ))}
            {form.allow_multiple && (
              <div className="ml-6 pt-1 flex items-center gap-2">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">
                  Max per cabinet
                </Label>
                <Input
                  type="number"
                  min={2}
                  step={1}
                  className="h-8 w-24"
                  value={form.max_quantity}
                  onChange={(e) => setField("max_quantity", e.target.value)}
                  placeholder="10"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {isEdit && isBuiltin && (
            <Button variant="ghost" onClick={handleRestoreBuiltin} disabled={saving}>
              Reset to built-in
            </Button>
          )}
          {isEdit && (
            <Button variant="destructive" onClick={handleHideOrDelete} disabled={saving}>
              {isBuiltin ? "Hide" : "Delete"}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccessoryEditorDialog;
